import { AsyncPipe, formatDate } from '@angular/common';
import { Component, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { isEqual, memoize } from 'lodash-es';
import { lastValueFrom, Observable, of, ReplaySubject } from 'rxjs';
import { CalendarComponent, Day, DayItem } from '@cumlaude/shared-components-inputs';
import { Level, unnest } from '../../../services/data-tree';
import {
	BasicFilterExpression,
	CompoundFilterExpression,
	DataOptions,
	DataService,
	ExportDataOptions,
	FilterExpression,
} from '../../../services/data.service';
import { generateCssClassForLesregistratie, getSchooljaarBegin } from '@cumlaude/shared-utils';
import { DetailsDashboard } from '../../base-details-panel/details.dashboard';
import { DashboardLeerling, LeerlingDetail } from '../../Details';
import { map, take } from 'rxjs/operators';
import { ExportOptions, ExportType } from '../../../services/export.service';
import { Table } from '@cumlaude/metadata';
import { TooltipElement } from '@cumlaude/shared-components-overlays';
import { DashboardHeaderComponent } from '../../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../../filter-panel/filter-panel.component';
import { FilterBarComponent } from '../../../filter-bar/filter-bar.component';
import { lookupByKeys } from '../../../services/tabulate';
import { createAggFunctions } from '../../../shared/dashboard/base-dashboard/base-dashboard.component';
import { Attributes, BaseDashboardConfig } from '../../../shared/dashboard/base-dashboard/base-dashboard-config';
import { noAgg0 } from '../../../services/aggregation';
import { FilterName } from '../../../services/filter-name';
import { LegendaComponent } from '../../../legenda/legenda.component';

interface LesregistratieA extends Attributes {
	aantalLesregistraties: number;
}

interface LesregistratieI extends Attributes {
	lr_nm_lesregistratie: string;
	lr_m_nr_leerlingen: number;
	lr_m_nr_lesregistraties: number; // aantal unieke lesregistraties
}

@Component({
	selector: 'app-leerling-lesregistraties-details-panel',
	templateUrl: './leerling-lesregistraties-details-panel.component.html',
	styleUrls: ['./leerling-lesregistraties-details-panel.component.scss'],
	imports: [FilterPanelComponent, DashboardHeaderComponent, CalendarComponent, AsyncPipe, FilterBarComponent, LegendaComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeerlingLesregistratiesDetailsPanelComponent
	extends BaseDashboardConfig<LesregistratieI, LesregistratieA>
	implements OnInit, DetailsDashboard<LeerlingDetail>
{
	protected readonly dataService = inject(DataService);

	leerling$ = new ReplaySubject<DashboardLeerling>(1);

	filters: FilterName[] = ['lr_d_datum.per_nm_schooljaar', 'lr_nm_lesregistratie'];

	@ViewChild(LegendaComponent)
	legendaComponent?: LegendaComponent;

	override singleAggregators = {
		aantalLesregistraties: noAgg0('lr_m_nr_lesregistraties'),
	};

	ngOnInit() {
		this.subscriptions.push(
			this.filterService.observeAsInput('lr_d_datum.per_nm_schooljaar').subscribe((val) => this.qp.dispatch('schooljaar', val))
		);
	}

	exportTypes = [ExportType.AFBEELDING, ExportType.PDF, ExportType.DATA];

	getSchooljaar() {
		return this.filterExpressions()
			.filter((value) => value instanceof BasicFilterExpression)
			.find((value) => isEqual(value.attr, ['lr_d_datum', 'per_nm_schooljaar']));
	}

	setSelected(selected: LeerlingDetail, schooljaar?: string) {
		this.leerling$.next({
			leerling: selected,
			schooljaarInfo: schooljaar ?? selected.lb_nm_schooljaar,
		});
	}

	getBeginDateForSchooljaar() {
		const schooljaar = this.getSchooljaar();
		return of(getSchooljaarBegin(schooljaar!.getValueString()));
	}

	doDetailsExport(exportOptions: ExportOptions) {
		lastValueFrom(this.leerling$.pipe(take(1))).then((leerling) =>
			this.doExport(this.filterExpressions(), this.getDashboardFilters(leerling.leerling), exportOptions)
		);
	}

	override getExportData(options: ExportDataOptions): Observable<Blob> {
		return this.dataService.getLesregistratieExportData(options);
	}

	factTable = Table.fac_lr_lesregistratie;

	getCalendarData = memoize(this._getCalendarData, (f, pf) => JSON.stringify([f, pf]));

	private _getCalendarData(
		filterExpressions: FilterExpression[],
		permanentFilterExpressions: FilterExpression[]
	): Observable<Level<LesregistratieA, number[]> | undefined> {
		return this.getData({
			g: [['lr_d_datum'], ['lr_nm_lesregistratie']],
			f: new CompoundFilterExpression([...filterExpressions, ...permanentFilterExpressions]),
		}).pipe(
			map((resp) => {
				const { aggInit, aggCombine } = createAggFunctions<LesregistratieI, LesregistratieA>(this, [], resp.measures);
				return unnest(resp.data, undefined, undefined, aggInit, aggCombine)[0]?.[0];
			})
		);
	}

	getData(options: DataOptions) {
		return this.dataService.getLesregistratieData(options);
	}

	getDashboardFilters = memoize(LeerlingLesregistratiesDetailsPanelComponent._getDashboardFilters, (l) => l.lb_nr_leerling);

	private static _getDashboardFilters(leerling: LeerlingDetail): FilterExpression[] {
		return [new BasicFilterExpression(['lr_nr_leerling'], leerling.lb_nr_leerling)];
	}

	getSchooljaarFilterOverride = memoize(LeerlingLesregistratiesDetailsPanelComponent._getSchooljaarFilterOverride);

	private static _getSchooljaarFilterOverride(schooljaarInfo: string) {
		return { 'lr_d_datum.per_nm_schooljaar': schooljaarInfo };
	}

	generateDay(date: Date, dataRoot?: Level<LesregistratieA, number[]>): Day {
		const geenLes = { date: new Date(date), classNames: [] };
		if (dataRoot === undefined) return geenLes;

		const dagLvl = lookupByKeys(dataRoot, [formatDate(date, 'yyyy-MM-dd', 'nl-NL')]);
		if (!dagLvl) return geenLes;

		const items: DayItem[] = [];
		const tooltip: TooltipElement[] = [];
		for (const key of dagLvl.c.map((lvl) => lvl.k)) {
			const value = lookupByKeys(dagLvl, [key])!;
			const aantal = value.a.aantalLesregistraties ?? 0;

			items.push({
				className: generateCssClassForLesregistratie(key) ?? '',
				weight: aantal,
			});

			tooltip.push({
				label: `Aantal ${key}`,
				value: `${aantal}`,
			});
		}
		return { date: new Date(date), items, tooltip };
	}
}
